<template>
    <div v-if="loadingSpinnerFlag" class="dash-loader">
        <div class="dash-loader__spinner"></div>
    </div>
    <div v-else class="dash">
        <div class="dash__container" :class="{ 'dash__container--wide': screenWidth > 1500 }">

            <!-- Weekly Cycle U19 -->
            <WeekTrainerCycleU19 class="w-full" />

            <!-- Main Grid -->
            <div class="dash__grid">

                <!-- LEFT SIDEBAR -->
                <div class="dash__sidebar">

                    <!-- Tournament Table -->
                    <div class="dash-panel">
                        <div class="dash-panel__header dash-panel__header--with-select">
                            <select
                                v-model="tournamentScope"
                                class="dash-select dash-select--sm"
                            >
                                <option
                                    v-for="option in competitionScopeOptions"
                                    :key="option.value"
                                    :value="option.value"
                                >{{ option.label }}</option>
                            </select>
                            <span class="dash-panel__title">ТУРНІРНА ТАБЛИЦЯ U19</span>
                        </div>
                        <table class="dash-table">
                            <thead>
                                <tr>
                                    <th class="dash-table__th dash-table__th--team">Команда</th>
                                    <th class="dash-table__th">І</th>
                                    <th class="dash-table__th">В</th>
                                    <th class="dash-table__th">Н</th>
                                    <th class="dash-table__th">П</th>
                                    <th class="dash-table__th">О</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-if="tournamentTable.length">
                                    <tr
                                        v-for="row in tournamentTable"
                                        :key="row.team"
                                        class="dash-table__row"
                                        :class="{ 'dash-table__row--highlight': isKolosRow(row) }"
                                    >
                                        <td class="dash-table__td dash-table__td--team">{{ row.team }}</td>
                                        <td class="dash-table__td">{{ row.played }}</td>
                                        <td class="dash-table__td">{{ row.wins }}</td>
                                        <td class="dash-table__td">{{ row.draws }}</td>
                                        <td class="dash-table__td">{{ row.losses }}</td>
                                        <td class="dash-table__td">{{ row.points }}</td>
                                    </tr>
                                </template>
                                <tr v-else>
                                    <td colspan="6" class="dash-table__empty">Відсутні дані</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Suspensions -->
                    <SuspensionsList :players="suspensionsData" />
                </div>

                <!-- MAIN CONTENT -->
                <div class="dash__main">

                    <!-- GPS Section Header -->
                    <div class="dash-section-label">
                        <span class="dash-section-label__text">GPS ДАНІ</span>
                        <span class="dash-section-label__line"></span>
                    </div>

                    <div class="dash__row dash__row--gps">
                        <div class="dash__col dash__col--gps-table">
                            <GpsStatsTable title="GPS / СЕЗОН" :data="gpsSeasonData">
                                <template #header-extra>
                                    <select
                                        v-model="gpsSelectedSeason"
                                        @change="onGpsSeasonChange"
                                        :disabled="gpsLoadingFlag"
                                        class="dash-select dash-select--sm"
                                        :class="{ 'opacity-50': gpsLoadingFlag }"
                                    >
                                        <option
                                            v-for="s in gpsSeasonOptions"
                                            :key="s.value"
                                            :value="s.value"
                                        >{{ s.label }}</option>
                                    </select>
                                </template>
                            </GpsStatsTable>
                        </div>
                        <div class="dash__col dash__col--gps-table">
                            <GpsStatsTable title="GPS / ОСТАННІЙ МАТЧ" :data="gpsLastMatchData" />
                        </div>
                        <div class="dash__col dash__col--gps-compare">
                            <GpsComparison :data="gpsComparisonData" />
                        </div>
                    </div>

                    <!-- Ratings Section Header -->
                    <div class="dash-section-label">
                        <span class="dash-section-label__text">РЕЙТИНГИ ГРАВЦІВ</span>
                        <span class="dash-section-label__line"></span>
                    </div>

                    <div class="dash__row dash__row--ratings">
                        <div class="dash__col dash__col--rating">
                            <PlayerRatingList
                                title="РЕЙТИНГ / СЕЗОН"
                                :players="ratingSeasonData"
                                show-scope-select
                                :scope="ratingSeasonScope"
                                show-metric-select
                                :metric="ratingSeasonMetric"
                                :disabled="ratingsLoadingFlag"
                                @update:scope="ratingSeasonScope = $event"
                                @update:metric="onRatingSeasonMetricChange"
                            />
                        </div>
                        <div class="dash__col dash__col--rating">
                            <PlayerRatingList
                                title="РЕЙТИНГ / ОСТАННІЙ МАТЧ"
                                :players="ratingLastMatchData"
                                show-scope-select
                                :scope="ratingLastMatchScope"
                                show-metric-select
                                :metric="ratingLastMatchMetric"
                                :disabled="ratingsLoadingFlag"
                                @update:scope="ratingLastMatchScope = $event"
                                @update:metric="onRatingLastMatchMetricChange"
                            />
                        </div>
                        <div class="dash__col dash__col--rating">
                            <PlayerRatingList
                                title="ГОЛ + ПАС"
                                :players="goalAssistData"
                                show-scope-select
                                :scope="goalAssistScope"
                                @update:scope="goalAssistScope = $event"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { onMounted, ref, computed } from 'vue'
import { useMainStore } from '../stores/mainStore'
import {
    parseUplU19Table,
    getSeasonGpsSummary,
    getLastMatchGpsSummary,
    getGpsComparison,
    getAllMatchReports,
    getAllMatchesByTrainerGroups,
    getGpsReportByMatchId,
    getPlayerGpsRatings,
    getGoalsAndAssists,
    getSuspendedPlayers
} from '@/desktopApp/services/apiRequests'

import WeekTrainerCycleU19 from '../components/WeekTrainerCycleU19.vue'
import GpsStatsTable from '../components/DashboardU19/GpsStatsTable.vue'
import GpsComparison from '../components/DashboardU19/GpsComparison.vue'
import PlayerRatingList, { COMPETITION_SCOPE_OPTIONS } from '../components/DashboardU19/PlayerRatingList.vue'
import SuspensionsList from '../components/DashboardU19/SuspensionsList.vue'

export default {
    name: 'MainViewU19',

    setup() {
        const store = useMainStore()
        const screenWidth = ref(window.innerWidth)
        const loadingSpinnerFlag = ref(true)
        const tournamentTable = ref([])
        const tournamentName = ref('')
        const allSeasons = ref([])
        const selectedSeasonId = ref(null)
        const tournamentScope = ref('championship')
        const competitionScopeOptions = COMPETITION_SCOPE_OPTIONS

        const seasonOptions = computed(() => allSeasons.value)

        const gpsSeasonData = ref({})
        const gpsLastMatchData = ref({})
        const gpsComparisonData = ref({})
        const gpsSelectedSeason = ref('')
        const gpsLoadingFlag = ref(false)
        const gpsMatchSeasons = ref([])

        const gpsSeasonOptions = computed(() => {
            const seasons = gpsMatchSeasons.value.length
                ? gpsMatchSeasons.value
                : allSeasons.value.map(s => s.season).filter(Boolean)

            const unique = [...new Set(seasons)]
            return unique.map(season => ({ value: season, label: season }))
        })

        const ratingSeasonData = ref([])
        const ratingLastMatchData = ref([])
        const goalAssistData = ref([])
        const suspensionsData = ref([])
        const ratingSeasonMetric = ref('total_distance')
        const ratingLastMatchMetric = ref('total_distance')
        const ratingSeasonScope = ref('championship')
        const ratingLastMatchScope = ref('championship')
        const goalAssistScope = ref('championship')
        const ratingsLoadingFlag = ref(false)

        const getU19Group = () => {
            return store.trainer_groups.find(g => /u-?19/i.test(g.name || ''))
                || store.trainer_groups[0]
        }

        const KOLOS_U19_TEAM_NAME = 'Kolos U19'

        const normalizeTeamLabel = (name) => {
            return String(name || '')
                .toLowerCase()
                .replace(/[\s_\-]+/g, ' ')
                .trim()
        }

        const isKolosU19TeamName = (name) => {
            const normalized = normalizeTeamLabel(name)
            if (!normalized) return false
            const hasKolos = normalized.includes('kolos') || normalized.includes('колос')
            const hasU19 = /\bu\s*19\b/.test(normalized) || normalized.replace(/\s+/g, '').includes('u19')
            return hasKolos && hasU19
        }

        const isKolosU19Match = (match) => {
            return isKolosU19TeamName(match?.firstTeamName) || isKolosU19TeamName(match?.secondTeamName)
        }

        const toNumber = (value) => {
            if (value == null || value === '') return null
            const num = Number(String(value).replace(',', '.').replace(/\s/g, ''))
            return Number.isFinite(num) ? num : null
        }

        const getSeasonVariants = (season) => {
            if (!season) return []
            const raw = String(season).trim()
            const variants = new Set([raw])
            const shortMatch = raw.match(/^(\d{2})\/(\d{2})$/)
            if (shortMatch) {
                variants.add(`${2000 + Number(shortMatch[1])}/${shortMatch[2]}`)
            }
            const longMatch = raw.match(/^(\d{4})\/(\d{2})$/)
            if (longMatch) {
                variants.add(`${longMatch[1].slice(-2)}/${longMatch[2]}`)
            }
            return [...variants]
        }

        const seasonsEqual = (a, b) => {
            const aVariants = getSeasonVariants(a)
            const bVariants = getSeasonVariants(b)
            return aVariants.some(v => bVariants.includes(v))
        }

        const getSeasonForApi = (season) => {
            if (!season) return null
            const variants = getSeasonVariants(season)
            return variants.find(v => /^\d{4}\/\d{2}$/.test(v)) || variants[0] || null
        }

        const GPS_METRIC_KEYS = [
            'total_distance', 'zone_4', 'zone_5', 'zone_6', 'speed_distance',
            'average_speed', 'max_speed', 'sprints', 'starts', 'stops'
        ]

        const emptyGpsSummary = () => ({
            total_distance: 0,
            zone_4: 0,
            zone_5: 0,
            zone_6: 0,
            speed_distance: 0,
            average_speed: 0,
            max_speed: 0,
            sprints: 0,
            starts: 0,
            stops: 0,
            matches_count: 0
        })

        const hasGpsMetrics = (data) => {
            if (!data) return false
            return GPS_METRIC_KEYS.some(key => {
                const num = toNumber(data[key])
                return num != null && num !== 0
            })
        }

        const listsToSummary = (summaryList, averageList, maxList) => {
            const pick = (primary, idx, secondary) => {
                const value = toNumber(primary?.[idx])
                if (value != null) return value
                return toNumber(secondary?.[idx]) ?? 0
            }

            return {
                total_distance: pick(summaryList, 0, averageList),
                max_speed: pick(maxList, 1, averageList),
                average_speed: pick(averageList, 2, summaryList),
                speed_distance: pick(summaryList, 3, averageList),
                zone_4: pick(summaryList, 4, averageList),
                zone_5: pick(summaryList, 5, averageList),
                zone_6: pick(summaryList, 6, averageList),
                sprints: pick(summaryList, 7, averageList),
                starts: pick(summaryList, 8, averageList),
                stops: pick(summaryList, 9, averageList),
                matches_count: 1
            }
        }

        const listHasValues = (list) => Array.isArray(list) && list.some(v => {
            const num = toNumber(v)
            return num != null && num !== 0
        })

        const aggregateFromPlayerGpsData = (players) => {
            const list = (players || []).filter(p => p?.player_gps_data)
            if (!list.length) return null

            const result = emptyGpsSummary()
            let avgSpeedSum = 0
            let avgSpeedCount = 0
            let hasAny = false

            const pointVal = (player, key) => toNumber(player?.player_gps_data?.[key]?.value)

            for (const player of list) {
                const values = {
                    total_distance: pointVal(player, 'first_gps_point'),
                    max_speed: pointVal(player, 'second_gps_point'),
                    average_speed: pointVal(player, 'third_gps_point'),
                    speed_distance: pointVal(player, 'fourth_gps_point'),
                    zone_4: pointVal(player, 'fifth_gps_point'),
                    zone_5: pointVal(player, 'sixth_gps_point'),
                    zone_6: pointVal(player, 'seventh_gps_point'),
                    sprints: pointVal(player, 'eighth_gps_point'),
                    starts: pointVal(player, 'nineth_gps_point'),
                    stops: pointVal(player, 'tenth_gps_point')
                }

                if (Object.values(values).some(v => v != null && v !== 0)) hasAny = true

                if (values.total_distance != null) result.total_distance += values.total_distance
                if (values.speed_distance != null) result.speed_distance += values.speed_distance
                if (values.zone_4 != null) result.zone_4 += values.zone_4
                if (values.zone_5 != null) result.zone_5 += values.zone_5
                if (values.zone_6 != null) result.zone_6 += values.zone_6
                if (values.sprints != null) result.sprints += values.sprints
                if (values.starts != null) result.starts += values.starts
                if (values.stops != null) result.stops += values.stops
                if (values.max_speed != null) result.max_speed = Math.max(result.max_speed, values.max_speed)
                if (values.average_speed != null) {
                    avgSpeedSum += values.average_speed
                    avgSpeedCount += 1
                }
            }

            if (!hasAny) return null

            result.average_speed = avgSpeedCount ? Number((avgSpeedSum / avgSpeedCount).toFixed(2)) : 0
            result.matches_count = 1
            return result
        }

        const extractGpsReport = (gpsRes) => {
            // Same shape as MainView / Matches / AddGpsReportForMatch:
            // { gps_report: { average_points_list, summary_points_list, gps_reports_list } }
            const report = gpsRes?.gps_report || gpsRes?.data?.gps_report || null
            if (!report) return null

            if (listHasValues(report.summary_points_list) || listHasValues(report.average_points_list)) {
                const fromLists = listsToSummary(
                    listHasValues(report.summary_points_list) ? report.summary_points_list : report.average_points_list,
                    report.average_points_list,
                    report.max_points_list
                )
                if (hasGpsMetrics(fromLists)) return fromLists
            }

            // Fallback: only average list (MainView path)
            if (listHasValues(report.average_points_list)) {
                const fromAvg = listsToSummary(
                    report.average_points_list,
                    report.average_points_list,
                    report.max_points_list || report.average_points_list
                )
                if (hasGpsMetrics(fromAvg)) return fromAvg
            }

            const players = [
                ...(report.gps_reports_list?.start_players || []),
                ...(report.gps_reports_list?.reserve_players || [])
            ]
            return aggregateFromPlayerGpsData(players)
        }

        const aggregateSeasonFromReports = (reports) => {
            const result = emptyGpsSummary()
            if (!reports.length) return result

            let avgSpeedSum = 0
            let avgSpeedCount = 0

            for (const report of reports) {
                result.total_distance += report.total_distance || 0
                result.zone_4 += report.zone_4 || 0
                result.zone_5 += report.zone_5 || 0
                result.zone_6 += report.zone_6 || 0
                result.speed_distance += report.speed_distance || 0
                result.sprints += report.sprints || 0
                result.starts += report.starts || 0
                result.stops += report.stops || 0
                result.max_speed = Math.max(result.max_speed, report.max_speed || 0)
                if (report.average_speed) {
                    avgSpeedSum += report.average_speed
                    avgSpeedCount += 1
                }
            }

            const divisor = reports.length
            // GPS / СЕЗОН: average per match (not season totals)
            result.total_distance = Math.round(result.total_distance / divisor)
            result.zone_4 = Math.round(result.zone_4 / divisor)
            result.zone_5 = Math.round(result.zone_5 / divisor)
            result.zone_6 = Math.round(result.zone_6 / divisor)
            result.speed_distance = Math.round(result.speed_distance / divisor)
            result.sprints = Math.round(result.sprints / divisor)
            result.starts = Math.round(result.starts / divisor)
            result.stops = Math.round(result.stops / divisor)
            result.average_speed = avgSpeedCount ? Number((avgSpeedSum / avgSpeedCount).toFixed(2)) : 0
            result.matches_count = reports.length
            return result
        }

        const buildComparison = (seasonData, lastMatchData) => {
            // seasonData already holds per-match averages
            const comparison = emptyGpsSummary()

            for (const key of GPS_METRIC_KEYS) {
                const seasonValue = Number(seasonData[key]) || 0
                const lastValue = Number(lastMatchData[key]) || 0
                if (!seasonValue) {
                    comparison[key] = lastValue ? 100 : 0
                } else {
                    comparison[key] = Number((((lastValue - seasonValue) / seasonValue) * 100).toFixed(1))
                }
            }

            return comparison
        }

        const unwrapGpsPayload = (res) => {
            if (!res || typeof res !== 'object') return emptyGpsSummary()

            const camelMap = {
                totalDistance: 'total_distance',
                zone4: 'zone_4',
                zone5: 'zone_5',
                zone6: 'zone_6',
                speedDistance: 'speed_distance',
                averageSpeed: 'average_speed',
                maxSpeed: 'max_speed',
                matchesCount: 'matches_count'
            }

            const normalize = (obj) => {
                if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null
                const result = emptyGpsSummary()
                let found = false
                for (const [key, value] of Object.entries(obj)) {
                    const snakeKey = camelMap[key] || key
                    if (!(snakeKey in result) && snakeKey !== 'matches_count') continue
                    const num = toNumber(value)
                    if (num == null) continue
                    result[snakeKey] = num
                    found = true
                }
                return found ? result : null
            }

            for (const candidate of [res, res.data, res.result, res.summary, res.gps_summary, res.metrics]) {
                const normalized = normalize(candidate)
                if (normalized && hasGpsMetrics(normalized)) return normalized
            }

            return emptyGpsSummary()
        }

        // Only Kolos U19 matches (by firstTeamName / secondTeamName), not by clubTeamId
        const loadKolosU19Matches = async () => {
            let matches = []

            try {
                const allReports = await getAllMatchReports()
                const list = Array.isArray(allReports) ? allReports : (allReports?.data || [])
                matches = list.filter(isKolosU19Match)
            } catch (e) {
                console.error('getAllMatchReports failed:', e)
            }

            if (!matches.length && store.trainer?.trainer_id) {
                try {
                    const trainerMatches = await getAllMatchesByTrainerGroups(store.trainer.trainer_id)
                    const list = Array.isArray(trainerMatches) ? trainerMatches : (trainerMatches?.data || [])
                    matches = list.filter(isKolosU19Match)
                } catch (e) {
                    console.error('getAllMatchesByTrainerGroups failed:', e)
                }
            }

            console.log('Kolos U19 matches loaded:', matches.length, matches.slice(0, 3).map(m => ({
                id: m.match_report_id,
                first: m.firstTeamName,
                second: m.secondTeamName,
                season: m.season,
                clubTeamId: m.clubTeamId
            })))

            return matches
        }

        const loadGpsDataFromMatches = async (season) => {
            const matches = await loadKolosU19Matches()
            console.log('GPS Kolos U19 matches:', matches.length)

            const seasons = [...new Set(matches.map(m => m.season).filter(Boolean))]
            seasons.sort((a, b) => String(b).localeCompare(String(a)))
            gpsMatchSeasons.value = seasons

            const seasonToUse = season || seasons[0] || ''
            if (seasonToUse && !gpsSelectedSeason.value) {
                gpsSelectedSeason.value = seasonToUse
            }

            const seasonMatches = seasonToUse
                ? matches.filter(m => seasonsEqual(m.season, seasonToUse))
                : matches

            seasonMatches.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
            console.log('GPS season matches:', seasonToUse, seasonMatches.length, seasonMatches.map(m => m.match_report_id))

            const reports = []
            for (const match of seasonMatches) {
                const matchId = match.match_report_id || match.id
                if (!matchId) continue
                try {
                    const gpsRes = await getGpsReportByMatchId(matchId)
                    console.log('GPS report raw for', matchId, gpsRes)
                    const summary = extractGpsReport(gpsRes)
                    if (summary) {
                        reports.push(summary)
                    }
                } catch (e) {
                    console.error('Failed to load GPS report for match:', matchId, e)
                }
            }

            console.log('GPS aggregated reports count:', reports.length, reports[0])

            const seasonSummary = aggregateSeasonFromReports(reports)
            const lastMatchSummary = reports[0] || emptyGpsSummary()

            gpsSeasonData.value = seasonSummary
            gpsLastMatchData.value = lastMatchSummary
            gpsComparisonData.value = buildComparison(seasonSummary, lastMatchSummary)
        }

        const loadGpsData = async (season) => {
            gpsLoadingFlag.value = true
            try {
                // Primary: aggregate GPS only for Kolos U19 matches
                await loadGpsDataFromMatches(season)

                if (hasGpsMetrics(gpsSeasonData.value) || hasGpsMetrics(gpsLastMatchData.value)) {
                    return
                }

                // Secondary: backend summary API scoped to Kolos U19 team name
                const apiSeason = getSeasonForApi(season)
                const variants = getSeasonVariants(season)
                const seasonsToTry = [...new Set([apiSeason, ...variants, season].filter(Boolean))]
                const group = getU19Group()
                const groupId = group?.group_id || null

                for (const seasonVariant of seasonsToTry) {
                    const [seasonRes, lastMatchRes, comparisonRes] = await Promise.all([
                        getSeasonGpsSummary(groupId, seasonVariant, KOLOS_U19_TEAM_NAME),
                        getLastMatchGpsSummary(groupId, seasonVariant, KOLOS_U19_TEAM_NAME),
                        getGpsComparison(groupId, seasonVariant, KOLOS_U19_TEAM_NAME)
                    ])

                    const seasonData = unwrapGpsPayload(seasonRes)
                    const lastMatchData = unwrapGpsPayload(lastMatchRes)
                    const comparisonData = unwrapGpsPayload(comparisonRes)

                    console.log('GPS summary API try', seasonVariant, { seasonRes, seasonData })

                    if (hasGpsMetrics(seasonData) || hasGpsMetrics(lastMatchData)) {
                        gpsSeasonData.value = seasonData
                        gpsLastMatchData.value = lastMatchData
                        gpsComparisonData.value = hasGpsMetrics(comparisonData)
                            ? comparisonData
                            : buildComparison(seasonData, lastMatchData)
                        return
                    }
                }
            } catch (e) {
                console.error('Failed to load GPS data:', e)
            } finally {
                gpsLoadingFlag.value = false
            }
        }

        const onGpsSeasonChange = () => {
            const season = gpsSelectedSeason.value || undefined
            loadGpsData(season)
            loadPlayerRatings(season)
        }

        const unwrapPlayersPayload = (res) => {
            if (!res) return []
            if (Array.isArray(res)) return res
            if (Array.isArray(res.players)) return res.players
            if (Array.isArray(res.data?.players)) return res.data.players
            if (Array.isArray(res.data)) return res.data
            return []
        }

        const loadPlayerRatings = async (season) => {
            const seasonToUse = season || gpsSelectedSeason.value || undefined
            const apiSeason = getSeasonForApi(seasonToUse) || seasonToUse
            ratingsLoadingFlag.value = true
            try {
                const [seasonRes, lastMatchRes, goalsRes, suspensionsRes] = await Promise.all([
                    getPlayerGpsRatings({
                        scope: 'season',
                        metric: ratingSeasonMetric.value,
                        season: apiSeason,
                        limit: 10
                    }),
                    getPlayerGpsRatings({
                        scope: 'last_match',
                        metric: ratingLastMatchMetric.value,
                        season: apiSeason,
                        limit: 10
                    }),
                    getGoalsAndAssists({
                        season: apiSeason,
                        limit: 10
                    }),
                    getSuspendedPlayers({
                        season: apiSeason,
                        matchesBack: 1
                    })
                ])

                ratingSeasonData.value = unwrapPlayersPayload(seasonRes)
                ratingLastMatchData.value = unwrapPlayersPayload(lastMatchRes)
                goalAssistData.value = unwrapPlayersPayload(goalsRes)
                suspensionsData.value = unwrapPlayersPayload(suspensionsRes)
            } catch (e) {
                console.error('Failed to load player ratings:', e)
            } finally {
                ratingsLoadingFlag.value = false
            }
        }

        const onRatingSeasonMetricChange = (metric) => {
            ratingSeasonMetric.value = metric
            loadPlayerRatings(gpsSelectedSeason.value || undefined)
        }

        const onRatingLastMatchMetricChange = (metric) => {
            ratingLastMatchMetric.value = metric
            loadPlayerRatings(gpsSelectedSeason.value || undefined)
        }

        const isKolosRow = (row) => {
            return (row.team || '').toLowerCase().includes('колос')
        }

        const onSeasonChange = () => {
            const season = allSeasons.value.find(s => s.season_id === selectedSeasonId.value)
            if (season) {
                tournamentTable.value = season.table || []
            }
        }

        onMounted(async () => {
            try {
                const uplResponse = await parseUplU19Table()
                if (uplResponse?.seasons?.length) {
                    allSeasons.value = uplResponse.seasons
                    tournamentName.value = uplResponse.tournament || 'ТУРНІРНА ТАБЛИЦЯ U19'
                    selectedSeasonId.value = uplResponse.seasons[0].season_id
                    tournamentTable.value = uplResponse.seasons[0].table || []
                }
            } catch (e) {
                console.error('Failed to load UPL U19 table:', e)
            }

            try {
                const matches = await loadKolosU19Matches()
                const seasons = [...new Set(matches.map(m => m.season).filter(Boolean))]
                seasons.sort((a, b) => String(b).localeCompare(String(a)))
                gpsMatchSeasons.value = seasons
                if (seasons.length) {
                    gpsSelectedSeason.value = seasons[0]
                }
            } catch (e) {
                console.error('Failed to preload GPS seasons from Kolos U19 matches:', e)
            }

            await loadGpsData(gpsSelectedSeason.value || undefined)
            await loadPlayerRatings(gpsSelectedSeason.value || undefined)

            loadingSpinnerFlag.value = false

            window.addEventListener('resize', () => {
                screenWidth.value = window.innerWidth
            })
        })

        return {
            store,
            screenWidth,
            loadingSpinnerFlag,
            tournamentTable,
            tournamentName,
            tournamentScope,
            competitionScopeOptions,
            seasonOptions,
            selectedSeasonId,
            onSeasonChange,
            gpsSeasonData,
            gpsLastMatchData,
            gpsComparisonData,
            gpsSelectedSeason,
            gpsSeasonOptions,
            gpsLoadingFlag,
            onGpsSeasonChange,
            ratingSeasonData,
            ratingLastMatchData,
            goalAssistData,
            ratingSeasonMetric,
            ratingLastMatchMetric,
            ratingSeasonScope,
            ratingLastMatchScope,
            goalAssistScope,
            ratingsLoadingFlag,
            onRatingSeasonMetricChange,
            onRatingLastMatchMetricChange,
            suspensionsData,
            isKolosRow
        }
    },

    components: {
        WeekTrainerCycleU19,
        GpsStatsTable,
        GpsComparison,
        PlayerRatingList,
        SuspensionsList
    }
}
</script>

<style scoped>
/* ────── Layout ────── */
.dash-loader {
    position: fixed;
    inset: 0;
    background: #1a1a1a;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
}
.dash-loader__spinner {
    width: 40px;
    height: 40px;
    border: 4px solid transparent;
    border-top-color: #C59014;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.dash {
    display: flex;
    justify-content: center;
    width: 100%;
    padding-bottom: 30px;
}
.dash__container {
    width: 100%;
    padding: 16px 20px 0;
}
.dash__container--wide {
    width: 93%;
    padding-left: 0;
    padding-right: 0;
}
.dash__grid {
    display: flex;
    gap: 16px;
    margin-top: 16px;
}
.dash__sidebar {
    width: 24%;
    min-width: 260px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    flex-shrink: 0;
}
.dash__main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.dash__row {
    display: flex;
    gap: 14px;
}
.dash__row--gps {
    /* equal height cards in the GPS row */
}
.dash__col--gps-table {
    flex: 1 1 38%;
    min-width: 0;
}
.dash__col--gps-compare {
    flex: 1 1 24%;
    min-width: 0;
}
.dash__col--rating {
    flex: 1 1 33.33%;
    min-width: 0;
}

/* ────── Section Labels ────── */
.dash-section-label {
    display: flex;
    align-items: center;
    gap: 12px;
}
.dash-section-label__text {
    color: #6b6b6b;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1.5px;
    white-space: nowrap;
}
.dash-section-label__line {
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, #3a3b3b, transparent);
}

/* ────── Shared Panel ────── */
.dash-panel {
    background: linear-gradient(145deg, #2a2b2b 0%, #252626 100%);
    border: 1px solid #3a3b3b;
    border-radius: 14px;
    padding: 14px 14px 10px;
}
.dash-panel__header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}
.dash-panel__header--with-select {
    position: relative;
    justify-content: center;
    min-height: 28px;
}
.dash-panel__header--with-select .dash-select {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
}
.dash-panel__title {
    color: #C59014;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.3px;
}

/* ────── Select ────── */
.dash-select {
    background: #333;
    color: #a68a3a;
    border: 1px solid #4a4a4a;
    border-radius: 8px;
    padding: 4px 10px;
    font-size: 12px;
    outline: none;
    cursor: pointer;
    transition: border-color 0.2s;
}
.dash-select:focus {
    border-color: #C59014;
}
.dash-select--sm {
    padding: 3px 8px;
    font-size: 12px;
}

/* ────── Tournament Table ────── */
.dash-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}
.dash-table__th {
    color: #a68a3a;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 4px 8px;
    text-align: center;
    border-bottom: 1px solid rgba(197, 144, 20, 0.3);
    letter-spacing: 0.3px;
}
.dash-table__th--team {
    text-align: left;
    padding-left: 8px;
    width: 40%;
}
.dash-table__row {
    transition: background 0.15s;
}
.dash-table__row:nth-child(odd) {
    background: rgba(58, 59, 59, 0.4);
}
.dash-table__row:hover {
    background: rgba(197, 144, 20, 0.08);
}
.dash-table__row--highlight {
    background: rgba(197, 144, 20, 0.12) !important;
    box-shadow: inset 3px 0 0 #C59014;
}
.dash-table__td {
    color: #b0a070;
    font-size: 13px;
    padding: 5px 4px;
    text-align: center;
}
.dash-table__td--team {
    text-align: left;
    padding-left: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 0;
}
.dash-table__empty {
    color: #666;
    font-size: 13px;
    text-align: center;
    padding: 14px 0;
}
</style>
